import { Envelope as Mail, Clock, ChatCircle as MessageCircle, Phone, MapPin, Shield, CurrencyDollar as DollarSign, Headphones } from '@phosphor-icons/react';
import { GoogleGIcon } from '@/components/icons/GoogleGIcon';
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { TikTokIcon } from "@/components/icons/TikTokIcon";
import { YouTubeIcon } from "@/components/icons/YouTubeIcon";
import { InstagramIcon } from "@/components/icons/InstagramIcon";
import { memo } from "react";
import { Link } from "@/lib/router-compat";
import { SEOHreflang } from "@/components/seo/SEOHreflang";
import { useTranslation } from "@/hooks/useTranslation";
import { useCountry } from "@/hooks/useCountry";
import { pageContent } from "@/utils/translations";
import { CONTACT_INFO, getWhatsAppLink } from "@/utils/contactInfo";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { buildBreadcrumbLd } from "@/utils/schemaMarkup";

const Contact = () => {
  const { language, t } = useTranslation();
  const { country } = useCountry();
  const content = pageContent[language];
  
  const basePath = country === 'UK' ? '/uk' : country === 'DE' ? '/de' : '';
  const baseUrl = 'https://roautomarket.ro';
  const canonicalPath = country === 'RO' ? '/contact' : `${basePath}/contact`;
  const canonicalUrl = `${baseUrl}${canonicalPath}`;
  
  const organizationLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "RO Auto Market",
    url: "https://roautomarket.ro/",
    email: "contact@roautomarket.ro",
    contactPoint: {
      "@type": "ContactPoint",
      email: "contact@roautomarket.ro",
      contactType: "customer service",
      availableLanguage: language === 'ro' ? "Romanian" : language === 'en-GB' ? "English" : "German"
    }
  };

  const breadcrumbLd = buildBreadcrumbLd([
    { name: t('nav.home') || 'Acasă', item: `${baseUrl}${basePath}` },
    { name: content.contact_title || 'Contact', item: canonicalUrl },
  ]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={language} />
        <title>{language === 'ro' ? 'Contact RO Auto Market — Suport Clienți și Dealeri' : `${content.contact_title} | RO Auto Market`}</title>
        <meta name="description" content={language === 'ro' ? 'Contactează echipa RO Auto Market. Suport pentru cumpărători și dealeri auto. Întrebări despre anunțuri, abonamente, import CSV sau parteneriate.' : content.contact_subtitle} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content={language === 'ro' ? 'Contactează-ne — RO Auto Market' : `${content.contact_title} | RO Auto Market`} />
        <meta property="og:description" content={language === 'ro' ? 'Suport rapid pentru cumpărători și dealeri auto din România.' : content.contact_subtitle} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${baseUrl}/og-roautomarket.png`} />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${content.contact_title} | RO Auto Market`} />
        <meta name="twitter:description" content={content.contact_subtitle} />
        <meta name="twitter:image" content={`${baseUrl}/og-roautomarket.png`} />
        
        <script type="application/ld+json">{JSON.stringify(organizationLd)}</script>
        {breadcrumbLd && <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>}
      </Helmet>
      <SEOHreflang canonicalUrl={canonicalUrl} />

      {/* Dark Premium Hero */}
      <section className="surface-dark relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy-2 to-brand-navy" />
        <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-5" />
        <div className="relative container mx-auto px-4 py-6 md:py-10">
          <Breadcrumbs items={[
            { label: content.contact_title, href: '#' }
          ]} className="mb-6 [&_a]:text-brand-cream/60 [&_span]:text-brand-cream/80" />
          
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-cream mb-4 font-serif italic">
              {content.contact_title}
            </h1>
            <p className="text-lg md:text-xl text-brand-cream/80">
              {content.contact_subtitle}
            </p>
          </div>
        </div>
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
      </section>

      <main className="container mx-auto px-4 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">

          {/* Two-column: Contact Info + Map */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Contact Details Card */}
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl font-serif italic">
                  <Mail className="text-primary" />
                  Date de Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-start gap-3">
                  <Mail size={18} className="text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <a href={`mailto:${CONTACT_INFO.email}`} className="font-semibold text-foreground hover:text-primary transition-colors">
                      {CONTACT_INFO.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={18} className="text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Telefon / WhatsApp</p>
                    <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:text-primary transition-colors">
                      {CONTACT_INFO.whatsappDisplay}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Adresă de contact în România</p>
                    <a 
                      href={CONTACT_INFO.googleMapsLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      {CONTACT_INFO.address}
                    </a>
                    <p className="mt-1 text-xs text-muted-foreground">Operatorul platformei este înregistrat în Regatul Unit. <Link to="/mentiuni-legale" className="underline">Vezi Mențiuni legale</Link>.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <GoogleGIcon size={18} className="mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">Google Business Profile</p>
                    <a
                      href={CONTACT_INFO.googleBusinessProfile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      Vezi profilul RO Auto Market pe Google
                    </a>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="flex flex-wrap gap-2">
                    <Button asChild className="bg-green-600 hover:bg-green-700">
                      <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-brand-cream">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                      </a>
                    </Button>
                    <Button asChild variant="outline">
                      <a href={`mailto:${CONTACT_INFO.email}`} className="inline-flex items-center gap-2">
                        <Mail size={16} />
                        {content.contact_send_email}
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Social Media */}
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-3">Urmărește-ne</p>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild variant="outline" size="sm">
                      <a href={CONTACT_INFO.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-blue-600"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        Facebook
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <a href={CONTACT_INFO.youtube} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5">
                        <YouTubeIcon size={14} className="text-brand-gold" />
                        YouTube
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <a href={CONTACT_INFO.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5">
                        <InstagramIcon size={14} className="text-pink-500" />
                        Instagram
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <a href={CONTACT_INFO.tiktok} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5">
                        <TikTokIcon size={14} />
                        TikTok
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Map Card */}
            <Card className="border-border/50 shadow-lg overflow-hidden">
              <CardContent className="p-0 h-full">
                <h2 className="sr-only">Locația RO Auto Market pe hartă</h2>
                <div className="h-full min-h-[350px]">

                  <iframe
                    src="https://www.google.com/maps?q=45.1497989,26.8348065&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '350px' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Locație RO Auto Market - Buzău"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Advantages Strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Headphones, title: "Îți răspundem.", desc: "24-48 de ore, de luni până vineri" },
              { icon: DollarSign, title: "0% Comision", desc: "Fără costuri ascunse" },
              { icon: Shield, title: "Firme verificate", desc: "Manual la ANAF și în RISCO" },
            ].map((item) => (
              <Card key={item.title} className="border-border/50 bg-card/50">
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon size={22} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <Card className="border-border/50">
              <CardContent className="text-center p-6">
                <Clock className="mx-auto mb-3 text-primary" size={24} />
                <p className="font-semibold">Timp de răspuns</p>
                <p className="text-sm text-muted-foreground">24-48 de ore, de luni până vineri</p>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="text-center p-6">
                <MessageCircle className="mx-auto mb-3 text-primary" size={24} />
                <p className="font-semibold">Îți răspundem.</p>
                <p className="text-sm text-muted-foreground">Prin email sau WhatsApp</p>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="text-center p-6">
                <MapPin className="mx-auto mb-3 text-primary" size={24} />
                <p className="font-semibold">Adresă de contact în România</p>
                <a 
                  href={CONTACT_INFO.googleMapsLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {CONTACT_INFO.addressShort}
                </a>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-primary/5 rounded-xl p-8 border border-primary/20 mb-8">
            <h2 className="text-xl font-semibold mb-2 text-primary font-serif italic">
              {content.contact_all_questions_title}
            </h2>
            <p className="text-muted-foreground mb-5">
              {content.contact_all_questions_desc}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
              <Button asChild size="lg" className="bg-brand-cream hover:bg-brand-cream/90 text-brand-navy tracking-widest uppercase font-semibold">
                <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  Trimite Mesaj
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href={`mailto:${CONTACT_INFO.email}`} className="inline-flex items-center gap-2">
                  <Mail size={16} />
                  {content.contact_email_button}
                </a>
              </Button>
            </div>
            
            <div className="mt-5 pt-4 border-t border-border">
              <div className="flex justify-center gap-4 text-sm flex-wrap">
                <Link to={`${basePath}/cars`} className="text-primary hover:underline">{content.contact_search_cars}</Link>
                <Link to={`${basePath}/pricing`} className="text-primary hover:underline">{content.contact_become_dealer}</Link>
                <Link to={country === 'RO' ? '/cum-functioneaza' : `${basePath}/how-it-works`} className="text-primary hover:underline">{content.contact_how_it_works}</Link>
                <Link to={`${basePath}/despre-noi`} className="text-primary hover:underline">{content.contact_about_us}</Link>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default memo(Contact);
